import React, {useCallback, useMemo, useState} from "react";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import IconButton from "../../../../../../components/base/button/icon_button";
import {CircularProgress, SvgIcon} from "@mui/material";
import {ReactComponent as More} from "../../../../../../../assets/images/more.svg";
import classnames from "classnames";
import {Popover} from "../../../../../../components/app-specific/popover";

export const AddressCardActionsV2 = {
    edit: 'edit',
    setDefault: 'setDefault',
    duplicate: 'duplicate',
    remove: 'remove',
    taxInfo: 'taxInfo',
};

const AddressCardActionsPopoverV2 = ({
                                         actions,
                                         edit,
                                         remove: removeProps,
                                         setDefault,
                                         duplicate,
                                         disabled,
                                         settingAsDefault,
                                     }) => {
    const [removing, setRemoving] = useState(false);
    const isMounted = useIsMounted();

    /**
     * Invokes the [removeProps] callback and manages the [removing] state to indicate that the remove action is in progress.
     * @type {(function(): Promise<void>)|*}
     */
    const remove = useCallback(async () => {
        setRemoving(true);
        await removeProps();
        if (!isMounted())
            return;
        setRemoving(false);
    }, [removeProps, isMounted])

    const closeDialogAndPerformAction = (close, action) => {
        close();
        action();
    }

    const popoverActions = useMemo(() => {
        const res = [];
        for (const action of actions) {
            switch (action) {
                case AddressCardActionsV2.edit:
                    if (!edit) continue;
                    res.push({
                        title: "Edit",
                        onClick: (close) => closeDialogAndPerformAction(close, edit),
                        className: "",
                    })
                    break;
                case AddressCardActionsV2.setDefault:
                    if (!setDefault) continue;
                    res.push({
                        loading: settingAsDefault,
                        loadingTitle: "Setting as Default...",
                        title: "Set as Default Shipping Address",
                        onClick: (close) => closeDialogAndPerformAction(close, setDefault),
                        className: "",
                    })
                    break;
                case AddressCardActionsV2.duplicate:
                    if (!duplicate) continue;
                    res.push({
                        title: "Duplicate Address",
                        onClick: (close) => closeDialogAndPerformAction(close, duplicate),
                        className: "",
                    })
                    break;
                case AddressCardActionsV2.remove:
                    if (!removeProps) continue;
                    res.push({
                        title: removing ? "Removing..." : "Remove",
                        onClick: remove,
                        className: "remove",
                    })
                    break;
                default:
                    break;
            }
        }
        return res;
    }, [actions, edit, setDefault, duplicate, removeProps, removing, remove, settingAsDefault])

    if (!popoverActions.length) {
        return null;
    }

    return (
        <Popover
            elevation={2}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
            }}
            child={(onEnter) =>
                <IconButton
                    onPressed={onEnter}
                    disabled={disabled}
                    color={'paper.secondary'}
                >
                    <SvgIcon component={More} className='icon small'/>
                </IconButton>
            }
            popoverChild={(_, close) => (
                <div className={'address-card-popover-actions'}>
                    {
                        popoverActions?.map(e => (
                            <div
                                key={e.title}
                                className={classnames(e.className, 'action-item', {'disabled': e.loading || disabled})}
                                onClick={() => !disabled && !e.loading && e.onClick(close)}
                            >
                                {
                                    e.loading &&
                                    <CircularProgress size={16} className={'me-2'}/>
                                }
                                <p>
                                    {
                                        e.loading
                                            ? e.loadingTitle ?? e.title ?? ""
                                            : e.title ?? ''
                                    }
                                </p>
                            </div>
                        ))
                    }
                </div>
            )}
        />
    );
}

export default AddressCardActionsPopoverV2;
